import {
  Box,
  VStack,
  Input,
  SimpleGrid,
  GridItem,
  Button,
  useColorModeValue,
  Checkbox,
  Flex,
  Stack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { FiDownload } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { DataTableFiltersProps, FilterConfig } from '../types'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

export function DataTableFilters({
  filters,
  values,
  onChange,
  onClear,
  showExport = false,
  onExport,
  showRefresh = false,
  onRefresh,
  refreshing = false,
  persistOption,
  layout = 'default',
}: DataTableFiltersProps) {
  const { t } = useTranslation()
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const handleFilterChange = (key: string, value: any) => {
    onChange({ ...values, [key]: value })
  }

  const hasActiveFilters = Object.values(values).some(
    value => value !== '' && value !== undefined && value !== null
  )

  const renderFilter = (filter: FilterConfig) => {
    const desktopWidth = filter.width
    const { key: filterKey, ...commonProps } = {
      key: filter.key,
      size: { base: 'sm', md: 'sm' } as any,
      w: { base: '100%', md: desktopWidth || 'auto' },
      minW: { base: 0, md: desktopWidth || '180px' },
      maxW: { base: '100%', md: desktopWidth || '220px' },
    }

    switch (filter.type) {
      case 'text':
        return (
          <Input
            key={filterKey}
            {...commonProps}
            placeholder={filter.placeholder || filter.label}
            value={values[filter.key] || ''}
            maxLength={filter.maxLength}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
          />
        )

      case 'select': {
        const placeholder = filter.placeholder || `Filter by ${filter.label}`

        return (
          <Box
            key={filterKey}
            w={{ base: '100%', md: desktopWidth || 'auto' }}
            minW={{ base: 0, md: desktopWidth || '180px' }}
            maxW={{ base: '100%', md: desktopWidth || '220px' }}
          >
            <DropdownSelect
              size="sm"
              value={String(values[filter.key] ?? '')}
              options={filter.options ?? []}
              placeholder={placeholder}
              emptyOptionLabel={placeholder}
              ariaLabel={filter.label}
              onChange={value => handleFilterChange(filter.key, value)}
            />
          </Box>
        )
      }

      case 'date':
        return (
          <Input
            key={filterKey}
            {...commonProps}
            type="date"
            placeholder={filter.placeholder || filter.label}
            value={values[filter.key] || ''}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
          />
        )

      case 'dateRange':
        return (
          <Stack
            key={filter.key}
            direction={{ base: 'column', sm: 'row' }}
            spacing={2}
            w="100%"
          >
            <Input
              size={{ base: 'sm', md: 'sm' } as any}
              type="date"
              placeholder="From"
              value={values[`${filter.key}_from`] || ''}
              onChange={(e) => handleFilterChange(`${filter.key}_from`, e.target.value)}
              w="100%"
              minW={{ base: 0, md: '150px' }}
            />
            <Input
              size={{ base: 'sm', md: 'sm' } as any}
              type="date"
              placeholder="To"
              value={values[`${filter.key}_to`] || ''}
              onChange={(e) => handleFilterChange(`${filter.key}_to`, e.target.value)}
              w="100%"
              minW={{ base: 0, md: '150px' }}
            />
          </Stack>
        )

      default:
        return null
    }
  }

  const renderActions = () => (
    <Flex
      wrap="wrap"
      columnGap={3}
      rowGap={2}
      justify={{ base: 'flex-start', md: 'flex-end' }}
      align="center"
      flexShrink={0}
    >
      <Button
        colorScheme="admin"
        size={{ base: 'sm', md: 'sm' }}
        onClick={onClear}
        isDisabled={!hasActiveFilters}
      >
        {t('common.clearFilters')}
      </Button>

      {showExport && onExport && (
        <Button
          colorScheme="green"
          size={{ base: 'sm', md: 'sm' }}
          leftIcon={<FiDownload />}
          onClick={onExport}
        >
          {t('common.exportCsv')}
        </Button>
      )}

      {showRefresh && onRefresh && (
        <RefreshIconButton
          label={t('common.refresh')}
          size={{ base: 'sm', md: 'sm' }}
          onClick={onRefresh}
          isLoading={refreshing}
        />
      )}
    </Flex>
  )

  if (layout === 'split') {
    const searchFilter = filters.find(filter => filter.key === 'search')
    const secondaryFilters = filters.filter(filter => filter.key !== 'search')
    const normalSecondaryFilters = secondaryFilters.filter(filter => filter.type !== 'dateRange')
    const dateRangeFilter = secondaryFilters.find(filter => filter.type === 'dateRange')

    return (
      <Box
        bg={cardBg}
        p={{ base: 4, md: 6 }}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
      >
        <VStack spacing={4} align="stretch">
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', lg: 'flex-start' }}
            gap={4}
          >
            <Wrap spacing={3} rowGap={2} align="center" flex="1" minW={0}>
              {persistOption && (
                <WrapItem>
                  <Checkbox
                    size={{ base: 'sm', md: 'sm' }}
                    isChecked={persistOption.checked}
                    onChange={(event) => persistOption.onChange(event.target.checked)}
                    colorScheme={persistOption.colorScheme || 'blue'}
                    whiteSpace="nowrap"
                  >
                    {persistOption.label}
                  </Checkbox>
                </WrapItem>
              )}
              {searchFilter && (
                <WrapItem flex="1" minW={{ base: 'full', md: '300px' }} maxW={{ base: 'full', xl: '620px' }}>
                  <Box w="100%">
                    {renderFilter(searchFilter)}
                  </Box>
                </WrapItem>
              )}
            </Wrap>
            {renderActions()}
          </Flex>

          {(normalSecondaryFilters.length > 0 || dateRangeFilter) && (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3}>
              {normalSecondaryFilters.map(filter => {
                const isMerchantField = filter.key.toLowerCase().includes('merchant')
                return (
                  <GridItem
                    key={filter.key}
                    minW={0}
                    colSpan={{ base: 1, md: isMerchantField ? 2 : 1, xl: isMerchantField ? 2 : 1 }}
                  >
                    {renderFilter(filter)}
                  </GridItem>
                )
              })}

              {dateRangeFilter && (
                <>
                  <GridItem colSpan={{ base: 1, md: 1, xl: 2 }}>
                    <Input
                      size={{ base: 'sm', md: 'sm' } as any}
                      type="date"
                      placeholder="From"
                      value={values[`${dateRangeFilter.key}_from`] || ''}
                      onChange={(e) =>
                        handleFilterChange(`${dateRangeFilter.key}_from`, e.target.value)
                      }
                    />
                  </GridItem>
                  <GridItem colSpan={{ base: 1, md: 1, xl: 2 }}>
                    <Input
                      size={{ base: 'sm', md: 'sm' } as any}
                      type="date"
                      placeholder="To"
                      value={values[`${dateRangeFilter.key}_to`] || ''}
                      onChange={(e) =>
                        handleFilterChange(`${dateRangeFilter.key}_to`, e.target.value)
                      }
                    />
                  </GridItem>
                </>
              )}
            </SimpleGrid>
          )}
        </VStack>
      </Box>
    )
  }

  return (
      <Box
        bg={cardBg}
        p={{ base: 4, md: 6 }}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
      >
      <VStack spacing={4} align="stretch">
        {/* Filter inputs in rows */}
        <Stack
          direction={{ base: 'column', md: 'row' }}
          spacing={{ base: 3, md: 4 }}
          align={{ base: 'stretch', md: 'flex-start' }}
          justify="space-between"
        >
          <Wrap
            spacing={{ base: 2, md: 3 }}
            rowGap={{ base: 2, md: 3 }}
            flex="1"
            minW={0}
          >
            {persistOption && (
              <WrapItem>
                <Checkbox
                  size={{ base: 'sm', md: 'sm' }}
                  isChecked={persistOption.checked}
                  onChange={(event) => persistOption.onChange(event.target.checked)}
                  colorScheme={persistOption.colorScheme || 'blue'}
                  whiteSpace="nowrap"
                >
                  {persistOption.label}
                </Checkbox>
              </WrapItem>
            )}
            {filters.map(filter => (
              <WrapItem
                key={filter.key}
                flexBasis={{ base: '100%', md: 'auto' }}
                w={{ base: '100%', md: 'auto' }}
                minW={0}
              >
                <Box w="100%" minW={0}>
                  {renderFilter(filter)}
                </Box>
              </WrapItem>
            ))}
          </Wrap>

          {renderActions()}
        </Stack>
      </VStack>
    </Box>
  )
}
